import { useEffect, useState } from "react";
import { LoadingDiv, SweetAlertShow } from "../master/Master";
import { studentFeeMismatchReport } from "../../api/Student";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import Master from "./Master";
import { Link } from "react-router-dom";

const MODULE_CONFIG = {
  batch_fee: {
    label: "Batch Fee",
    icon: "ri-graduation-cap-line",
    color: "primary",
  },
  hostel_fee: {
    label: "Hostel Fee",
    icon: "ri-hotel-bed-line",
    color: "success",
  },
  transport_fee: {
    label: "Transport Fee",
    icon: "ri-bus-line",
    color: "warning",
  },
  library_fee: { label: "Library Fee", icon: "ri-book-2-line", color: "info" },
};

export default function StudentFeeMismatchReport() {
  const [loading, setLoading] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [filters, setFilters] = useState({});
  const [data, setData] = useState({});
  const [moduleFilter, setModuleFilter] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const parseSessions = JSON.parse(localStorage.getItem("sessions") ?? "[]");
    setSessions(parseSessions);
  }, []);

  useEffect(() => {
    const activeSession = sessions?.find((s) => Number(s.status) === 1)?.id;
    setFilters((prev) => {
      if (prev.session_id || !activeSession) return prev;
      return { ...prev, session_id: activeSession };
    });
  }, [sessions]);

  const getMismatchReport = async () => {
    setLoading(true);
    try {
      let addUrl = "";
      Object.keys(filters).forEach((key) => {
        const value = filters[key];
        if (value) addUrl += `&${key}=${encodeURIComponent(value)}`;
      });
      const response = await studentFeeMismatchReport(addUrl);
      if (response.data.status) {
        setData(response?.data?.data || {});
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.response?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getMismatchReport();
  }, [filters]);

  const inr = (n) =>
    "₹ " +
    Number(n || 0).toLocaleString("en-IN", {
      maximumFractionDigits: 2,
    });

  const allMismatches = data?.mismatches || [];

  // ===========================
  // Per-module counts (for the tab badges — computed from the full list,
  // independent of the currently active tab/search)
  // ===========================
  const moduleCounts = allMismatches.reduce((acc, m) => {
    acc[m.module] = (acc[m.module] || 0) + 1;
    return acc;
  }, {});

  const filteredMismatches = allMismatches.filter((m) => {
    if (moduleFilter !== "all" && m.module !== moduleFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      const hay =
        `${m.student_name || ""} ${m.registration_no || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const totalStoredHigher = allMismatches.filter(
    (m) => m.status === "stored_higher",
  ).length;
  const totalTxnHigher = allMismatches.filter(
    (m) => m.status === "transactions_higher",
  ).length;

  return (
    <>
      {loading && <LoadingDiv />}
      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />
          <div className="layout-page">
            <Nav />
            <div className="content-wrapper">
              <div className="container-fluid flex-grow-1 container-p-y">
                <div className="row">
                  <Master />
                  <div className="col-12">
                    {/* Header / Filters */}
                    <div className="card border-0 shadow-sm rounded-4 p-3 mb-3">
                      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                        <div className="d-flex align-items-center gap-3">
                          <div
                            className="bg-danger text-white rounded-3 d-flex align-items-center justify-content-center fs-5"
                            style={{ width: 46, height: 46 }}
                          >
                            <i className="ri-error-warning-line"></i>
                          </div>
                          <div>
                            <h5 className="m-0 fw-bold">Fee Mismatch Report</h5>
                            <small className="text-muted">
                              Records where the stored paid amount doesn't match
                              transactions
                            </small>
                          </div>
                        </div>

                        <div className="d-flex align-items-center gap-2 flex-wrap py-2">
                          <div className="form-floating form-floating-outline">
                            <select
                              className="form-select rounded-3"
                              value={filters?.session_id || ""}
                              onChange={(e) =>
                                setFilters({
                                  ...filters,
                                  session_id: e.target.value,
                                })
                              }
                            >
                              {sessions?.map((s) => (
                                <option key={s.id} value={s.id}>
                                  {s.session} :{" "}
                                  {s.status === "1" ? "Active" : "Inactive"}
                                </option>
                              ))}
                            </select>
                            <label>Session</label>
                          </div>

                          <div className="form-floating form-floating-outline">
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Search"
                              value={search}
                              onChange={(e) => setSearch(e.target.value)}
                              style={{ minWidth: 220 }}
                            />
                            <label>Search student / reg. no</label>
                          </div>

                          <button
                            className="btn btn-outline-secondary"
                            onClick={getMismatchReport}
                            title="Refresh"
                          >
                            <i className="ri-refresh-line"></i>
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* ============ Summary Cards ============ */}
                    <div className="row g-3 mb-3">
                      <div className="col-6 col-md-3">
                        <div
                          className="card border-0 shadow-sm rounded-4 h-100"
                          style={{ borderTop: "3px solid #dc3545" }}
                        >
                          <div className="card-body p-3">
                            <div
                              className="rounded-3 d-flex align-items-center justify-content-center mb-2 fs-6"
                              style={{
                                width: 42,
                                height: 42,
                                background: "#dc35451a",
                                color: "#dc3545",
                              }}
                            >
                              <i className="ri-error-warning-line"></i>
                            </div>
                            <h5 className="m-0 fw-bold text-danger">
                              {data?.total_mismatches || 0}
                            </h5>
                            <small className="text-muted text-uppercase fw-semibold">
                              Total Mismatches
                            </small>
                          </div>
                        </div>
                      </div>

                      <div className="col-6 col-md-3">
                        <div
                          className="card border-0 shadow-sm rounded-4 h-100"
                          style={{ borderTop: "3px solid #f59e0b" }}
                        >
                          <div className="card-body p-3">
                            <div
                              className="rounded-3 d-flex align-items-center justify-content-center mb-2 fs-6"
                              style={{
                                width: 42,
                                height: 42,
                                background: "#f59e0b1a",
                                color: "#f59e0b",
                              }}
                            >
                              <i className="ri-arrow-up-line"></i>
                            </div>
                            <h5
                              className="m-0 fw-bold"
                              style={{ color: "#f59e0b" }}
                            >
                              {totalStoredHigher}
                            </h5>
                            <small className="text-muted text-uppercase fw-semibold">
                              Stored Higher
                            </small>
                          </div>
                        </div>
                      </div>

                      <div className="col-6 col-md-3">
                        <div
                          className="card border-0 shadow-sm rounded-4 h-100"
                          style={{ borderTop: "3px solid #0ea5e9" }}
                        >
                          <div className="card-body p-3">
                            <div
                              className="rounded-3 d-flex align-items-center justify-content-center mb-2 fs-6"
                              style={{
                                width: 42,
                                height: 42,
                                background: "#0ea5e91a",
                                color: "#0ea5e9",
                              }}
                            >
                              <i className="ri-arrow-down-line"></i>
                            </div>
                            <h5
                              className="m-0 fw-bold"
                              style={{ color: "#0ea5e9" }}
                            >
                              {totalTxnHigher}
                            </h5>
                            <small className="text-muted text-uppercase fw-semibold">
                              Transactions Higher
                            </small>
                          </div>
                        </div>
                      </div>

                      <div className="col-6 col-md-3">
                        <div
                          className="card border-0 shadow-sm rounded-4 h-100"
                          style={{ borderTop: "3px solid #10b981" }}
                        >
                          <div className="card-body p-3">
                            <div
                              className="rounded-3 d-flex align-items-center justify-content-center mb-2 fs-6"
                              style={{
                                width: 42,
                                height: 42,
                                background: "#10b9811a",
                                color: "#10b981",
                              }}
                            >
                              <i className="ri-scales-3-line"></i>
                            </div>
                            <h5
                              className="m-0 fw-bold"
                              style={{ color: "#10b981" }}
                            >
                              {inr(
                                filteredMismatches.reduce(
                                  (t, r) =>
                                    t + Math.abs(Number(r.difference || 0)),
                                  0,
                                ),
                              )}
                            </h5>
                            <small className="text-muted text-uppercase fw-semibold">
                              Total Difference
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* ============ Module Tabs ============ */}
                    <div className="card border-0 shadow-sm rounded-4 overflow-hidden mb-3">
                      <div className="d-flex align-items-center px-3 pt-3 gap-2 flex-wrap border-bottom pb-3">
                        <button
                          className={`btn btn-sm rounded-pill px-3 ${
                            moduleFilter === "all"
                              ? "btn-dark"
                              : "btn-outline-secondary"
                          }`}
                          onClick={() => setModuleFilter("all")}
                        >
                          All
                          <span className="badge bg-light text-dark ms-2">
                            {allMismatches.length}
                          </span>
                        </button>

                        {Object.entries(MODULE_CONFIG).map(([key, cfg]) => (
                          <button
                            key={key}
                            className={`btn btn-sm rounded-pill px-3 ${
                              moduleFilter === key
                                ? `btn-${cfg.color}`
                                : `btn-outline-${cfg.color}`
                            }`}
                            onClick={() => setModuleFilter(key)}
                          >
                            <i className={`${cfg.icon} me-1`}></i>
                            {cfg.label}
                            <span
                              className={`badge ms-2 ${
                                moduleFilter === key
                                  ? "bg-white text-dark"
                                  : `bg-${cfg.color}-subtle text-${cfg.color}`
                              }`}
                            >
                              {moduleCounts[key] || 0}
                            </span>
                          </button>
                        ))}
                      </div>

                      {/* ============ Table ============ */}
                      <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0">
                          <thead>
                            <tr className="text-uppercase small text-muted">
                              <th className="ps-4 py-3 fw-semibold">#</th>
                              <th className="py-3 fw-semibold">Student</th>
                              <th className="py-3 fw-semibold">Module</th>
                              <th className="py-3 fw-semibold">Record ID</th>
                              <th className="py-3 fw-semibold text-end">
                                Stored Paid
                              </th>
                              <th className="py-3 fw-semibold text-end">
                                Transaction Paid
                              </th>
                              <th className="py-3 fw-semibold text-end">
                                Difference
                              </th>
                              <th className="py-3 fw-semibold pe-4">Status</th>
                            </tr>
                          </thead>

                          <tbody>
                            {filteredMismatches.length > 0 ? (
                              filteredMismatches.map((m, index) => {
                                const cfg = MODULE_CONFIG[m.module] || {
                                  label: m.module_label,
                                  icon: "ri-price-tag-3-line",
                                  color: "secondary",
                                };
                                const higher = m.status === "stored_higher";

                                return (
                                  <tr key={`${m.module}-${m.record_id}`}>
                                    <td className="ps-4">{index + 1}</td>
                                    <td>
                                      {m.student_id ? (
                                        <Link
                                          className="fw-semibold text-decoration-none"
                                          to={`/student/details?id=${m.student_id}`}
                                        >
                                          {m.student_name || "-"}{" "}
                                          <span className="text-muted fw-normal">
                                            ({m.registration_no || "-"})
                                          </span>
                                        </Link>
                                      ) : (
                                        <span className="fw-semibold">
                                          {m.student_name || "-"}
                                        </span>
                                      )}
                                    </td>
                                    <td>
                                      <span
                                        className={`badge bg-${cfg.color}-subtle text-${cfg.color} rounded-pill fw-semibold`}
                                      >
                                        <i className={`${cfg.icon} me-1`}></i>
                                        {cfg.label || m.module_label}
                                      </span>
                                    </td>
                                    <td className="text-muted">
                                      {m.record_id}
                                    </td>
                                    <td className="text-end fw-semibold">
                                      {inr(m.stored_paid)}
                                    </td>
                                    <td className="text-end fw-semibold">
                                      {inr(m.transaction_paid)}
                                    </td>
                                    <td
                                      className={`text-end fw-bold ${
                                        higher ? "text-warning" : "text-info"
                                      }`}
                                    >
                                      {higher ? "+" : "-"}
                                      {inr(Math.abs(m.difference))}
                                    </td>
                                    <td className="pe-4">
                                      <span
                                        className={`badge rounded-pill ${
                                          higher
                                            ? "bg-warning-subtle text-warning-emphasis"
                                            : "bg-info-subtle text-info-emphasis"
                                        }`}
                                      >
                                        {higher
                                          ? "Record shows more paid"
                                          : "Transactions show more paid"}
                                      </span>
                                    </td>
                                  </tr>
                                );
                              })
                            ) : (
                              <tr>
                                <td
                                  colSpan="8"
                                  className="text-center text-muted py-5"
                                >
                                  <i className="ri-checkbox-circle-line fs-2 d-block mb-2 text-success"></i>
                                  {allMismatches.length === 0
                                    ? "No mismatches found — all records reconcile cleanly."
                                    : "No mismatches match the current filters."}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
